/**
 * スポット画像の可用性をチェックするユーティリティ
 */

import { Spot } from '../types/core';

/**
 * スポットに有効な画像URLがあるかチェック
 */
export function hasValidImage(spot: Spot): boolean {
    const sources = [
        spot.image?.url,
        (spot as any).photo?.pc?.l,
        (spot as any).photo?.mobile?.l,
        (spot as any).photo?.s,
        (spot as any).image,
        (spot as any).imageUrl,
        // Hot Pepper API形式
        (spot as any).photo_url,
        (spot as any).logo_image,
        // Yahoo! API形式
        (spot as any).Image,
        (spot as any).LeadImage
    ];

    const validUrl = sources.find(url => url && typeof url === 'string' && url.startsWith('http'));
    return !!validUrl;
}

/**
 * 画像がなくても許可するジャンル
 */
const ALLOWED_WITHOUT_IMAGE_GENRES = [
    'museum',
    'bookstore',
    'park',
    'event',
    'sweets'  // スイーツ店も画像がない場合が多い
];

/**
 * スポットが画像なしでも含めるべきかチェック
 */
export function shouldIncludeWithoutImage(spot: Spot): boolean {
    return ALLOWED_WITHOUT_IMAGE_GENRES.includes(spot.genre);
}

/**
 * スポットが表示可能かチェック（画像ありまたは許可ジャンル）
 */
export function isSpotDisplayable(spot: Spot): boolean {
    return hasValidImage(spot) || shouldIncludeWithoutImage(spot);
}

/**
 * Google Places APIで画像取得を試行すべきスポットかチェック
 */
export function shouldTryGooglePlacesImage(spot: Spot): boolean {
    // 画像がなく、かつ許可ジャンルの場合はGoogle Places APIを試行
    return !hasValidImage(spot) && shouldIncludeWithoutImage(spot);
}

/**
 * スポット配列から表示可能なもののみをフィルタリング
 */
export function filterSpotsWithImages(spots: Spot[]): Spot[] {
    return spots.filter(spot => isSpotDisplayable(spot));
}

/**
 * プランのスポットを表示可能性でフィルタリング
 */
export function filterPlanSpotsByImages(plan: any): any {
    if (!plan || !plan.steps) return plan;

    const filteredSteps = plan.steps.filter((step: any) => {
        if (step.type === 'spot') {
            return isSpotDisplayable(step);
        }
        return true; // walk ステップはそのまま保持
    });

    return {
        ...plan,
        steps: filteredSteps
    };
}

/**
 * 複数プランの表示可能性フィルタリング
 */
export function filterPlansWithImages(plans: any[]): any[] {
    return plans
        .map(plan => filterPlanSpotsByImages(plan))
        .filter(plan => {
            // 少なくとも1つのスポットがあるプランのみ保持
            const spotCount = plan.steps.filter((step: any) => step.type === 'spot').length;
            return spotCount > 0;
        });
}